import * as XLSX from "xlsx";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export interface BackupSheet {
  name: string;
  data: Record<string, unknown>[];
}

export interface BackupOptions {
  restaurantName: string;
  startDate: Date;
  endDate: Date;
}

// Format date for display in Excel
export function formatDateForExcel(date: string | Date | null | undefined): string {
  if (!date) return "";
  try {
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR });
  } catch {
    return String(date);
  }
}

// Format date only (no time)
export function formatDateOnly(date: string | Date | null | undefined): string {
  if (!date) return "";
  try {
    return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return String(date);
  }
}

// Format currency for display
export function formatCurrencyForExcel(value: number | null | undefined): string {
  if (value === null || value === undefined) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

// Format number for Excel (actual number, not string)
export function formatNumberForExcel(value: number | null | undefined): number {
  return value ?? 0;
}

// Generate multi-sheet Excel file
export function generateBackupExcel(sheets: BackupSheet[], options: BackupOptions): void {
  const workbook = XLSX.utils.book_new();

  // Add summary sheet first
  const summaryData = [
    { Campo: "Restaurante", Valor: options.restaurantName },
    { Campo: "Período Inicial", Valor: formatDateOnly(options.startDate) },
    { Campo: "Período Final", Valor: formatDateOnly(options.endDate) },
    { Campo: "Data de Geração", Valor: formatDateForExcel(new Date()) },
    { Campo: "", Valor: "" },
    { Campo: "Resumo de Dados", Valor: "" },
    ...sheets.map((sheet) => ({
      Campo: sheet.name,
      Valor: `${sheet.data.length} registros`,
    })),
  ];

  const summarySheet = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Resumo");

  // Add each data sheet
  for (const sheet of sheets) {
    if (sheet.data.length === 0) {
      // Add empty sheet with header message
      const emptySheet = XLSX.utils.json_to_sheet([{ Mensagem: "Nenhum registro encontrado no período" }]);
      XLSX.utils.book_append_sheet(workbook, emptySheet, sheet.name.substring(0, 31));
    } else {
      // Limit to 50000 rows to prevent browser crash
      const limitedData = sheet.data.slice(0, 50000);
      const worksheet = XLSX.utils.json_to_sheet(limitedData);
      
      // Auto-fit columns
      const maxWidth = 50;
      const colWidths = Object.keys(limitedData[0] || {}).map((key) => {
        const maxLength = Math.max(
          key.length,
          ...limitedData.slice(0, 100).map((row) => String(row[key] || "").length)
        );
        return { wch: Math.min(maxLength + 2, maxWidth) };
      });
      worksheet["!cols"] = colWidths;

      // Sheet name max 31 chars
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name.substring(0, 31));
    }
  }

  // Generate filename
  const dateStr = format(new Date(), "yyyy-MM-dd", { locale: ptBR });
  const safeRestaurantName = options.restaurantName
    .replace(/[^a-zA-Z0-9]/g, "_")
    .substring(0, 20);
  const filename = `backup-${safeRestaurantName}-${dateStr}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, filename);
}

// Calculate total records across all sheets
export function calculateTotalRecords(sheets: BackupSheet[]): number {
  return sheets.reduce((total, sheet) => total + sheet.data.length, 0);
}
